import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ArrowLeft, Camera, Paperclip } from 'lucide-react';
import { Button } from '../../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { getUserAvatarUrl } from '../../../utils/api';
import { formatLastSeen } from '../../../hooks/useCrmChat';
import { groupMessagesWithDateSeparators } from '../../../utils/formatChatDateSeparator';
import { isAllowedChatFile } from '../../../utils/crmChatImageCompress';
import { isMobileContainer } from '../../../utils/device';
import CrmChatMessageBubble from './CrmChatMessageBubble';
import CrmChatDateSeparator from './CrmChatDateSeparator';
import CrmChatReplyPreview from './CrmChatReplyPreview';
import CrmChatInput from './CrmChatInput';
import CrmChatCameraModal from './CrmChatCameraModal';
import CrmChatImageModal from './CrmChatImageModal';
import CrmChatNewMessagesPill from './CrmChatNewMessagesPill';

const NEAR_BOTTOM_PX = 80;

function getInitials(name) {
  return (name || 'U')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function checkNearBottom(el) {
  if (!el) return true;
  return el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
}

export default function CrmChatConversation({
  contact,
  messages,
  loadingMessages,
  loadingOlder,
  hasMoreMessages,
  onBack,
  onSend,
  onSendAttachment,
  onLoadOlder,
  replyTo,
  onCancelReply,
  onReply,
  onScrollToReply,
  uploadProgress,
  highlightedMessageId,
  registerMessageRef,
  listContainerRef,
  showBackButton = false,
  pendingNewCount = 0,
  onNearBottomChange,
  onClearPendingNew,
  consumeJustSent,
}) {
  const localListRef = useRef(null);
  const listRef = listContainerRef || localListRef;
  const prevLengthRef = useRef(0);
  const prevScrollHeightRef = useRef(0);
  const isNearBottomLocalRef = useRef(true);
  const fileInputRef = useRef(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [imageModalMessage, setImageModalMessage] = useState(null);
  const isMobile = isMobileContainer();

  const listItems = groupMessagesWithDateSeparators(messages);

  const updateNearBottom = useCallback(() => {
    const el = listRef.current;
    const near = checkNearBottom(el);
    isNearBottomLocalRef.current = near;
    onNearBottomChange?.(near);
  }, [listRef, onNearBottomChange]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const grew = messages.length > prevLengthRef.current;
    const justSent = consumeJustSent?.() ?? false;
    const shouldStick = justSent || isNearBottomLocalRef.current;

    if (grew && !loadingOlder && shouldStick) {
      el.scrollTop = el.scrollHeight;
      isNearBottomLocalRef.current = true;
      onNearBottomChange?.(true);
      onClearPendingNew?.();
    } else if (loadingOlder && prevScrollHeightRef.current) {
      el.scrollTop = el.scrollHeight - prevScrollHeightRef.current;
    }

    prevLengthRef.current = messages.length;
    prevScrollHeightRef.current = el.scrollHeight;
  }, [messages, loadingOlder, listRef, consumeJustSent, onNearBottomChange, onClearPendingNew]);

  // Reset length tracking when switching conversations
  useEffect(() => {
    prevLengthRef.current = 0;
    prevScrollHeightRef.current = 0;
    isNearBottomLocalRef.current = true;
    onNearBottomChange?.(true);
  }, [contact?.conversation_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleScroll = () => {
    const el = listRef.current;
    if (!el) return;
    updateNearBottom();
    if (loadingOlder || !hasMoreMessages) return;
    if (el.scrollTop < 40) {
      prevScrollHeightRef.current = el.scrollHeight;
      onLoadOlder();
    }
  };

  const scrollToLatest = () => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    isNearBottomLocalRef.current = true;
    onNearBottomChange?.(true);
    onClearPendingNew?.();
  };

  const handleFilePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!isAllowedChatFile(file)) {
      window.alert('File type not allowed.');
      return;
    }
    await onSendAttachment?.(file);
  };

  const statusText = contact?.is_online
    ? 'Online'
    : formatLastSeen(contact?.last_seen_at);

  return (
    <div className="flex flex-col h-full flex-1 min-w-0 bg-background">
      <div className="flex items-center gap-3 px-3 py-3 border-b border-border bg-card shrink-0">
        {showBackButton && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0 md:hidden"
            onClick={onBack}
            aria-label="Back to employee list"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
        )}
        <Avatar className="h-10 w-10 shrink-0">
          {contact?.avatar ? (
            <AvatarImage src={getUserAvatarUrl(contact.avatar)} alt={contact.name} />
          ) : null}
          <AvatarFallback className="bg-muted text-foreground text-sm">
            {getInitials(contact?.name)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-foreground truncate">{contact?.name}</div>
          <div className="text-xs text-muted-foreground truncate">
            {contact?.designation || contact?.role_name || 'CRM Employee'}
          </div>
          <div
            className={`text-[11px] truncate ${
              contact?.is_online ? 'text-emerald-600' : 'text-muted-foreground'
            }`}
          >
            {statusText}
          </div>
        </div>
      </div>

      <div className="relative flex-1 min-h-0 flex flex-col">
        <div
          ref={listRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto px-3 py-4 space-y-2 bg-[linear-gradient(180deg,hsl(var(--muted)/0.25)_0%,transparent_120px)]"
        >
          {loadingOlder && (
            <div className="text-center text-xs text-muted-foreground py-2">Loading older messages...</div>
          )}
          {loadingMessages && messages.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-8">Loading messages...</div>
          ) : messages.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-8">
              No messages yet. Say hello to {contact?.name?.split(' ')[0] || 'your colleague'}.
            </div>
          ) : (
            listItems.map((item) => {
              if (item.type === 'separator') {
                return <CrmChatDateSeparator key={item.id} label={item.label} />;
              }
              const m = item.message;
              return (
                <CrmChatMessageBubble
                  key={m.id}
                  message={m}
                  highlighted={highlightedMessageId === m.id}
                  uploadProgress={m._pending ? uploadProgress : 0}
                  onReply={onReply}
                  onScrollToReply={onScrollToReply}
                  onOpenImage={setImageModalMessage}
                  registerRef={registerMessageRef}
                />
              );
            })
          )}
        </div>
        <CrmChatNewMessagesPill count={pendingNewCount} onClick={scrollToLatest} />
      </div>

      <CrmChatReplyPreview replyTo={replyTo} onCancel={onCancelReply} />

      <CrmChatInput
        onSend={onSend}
        disabled={!contact?.conversation_id}
        replyTo={replyTo}
        leadingActions={(
          <>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".png,.jpg,.jpeg,.webp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar,image/*"
              onChange={handleFilePick}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-10 w-10 shrink-0 rounded-full"
              disabled={!contact?.conversation_id}
              onClick={() => fileInputRef.current?.click()}
              aria-label="Attach file"
            >
              <Paperclip className="h-5 w-5 text-muted-foreground" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-10 w-10 shrink-0 rounded-full"
              disabled={!contact?.conversation_id}
              onClick={() => setCameraOpen(true)}
              aria-label="Camera"
            >
              <Camera className="h-5 w-5 text-muted-foreground" />
            </Button>
          </>
        )}
      />

      <CrmChatCameraModal
        open={cameraOpen}
        onOpenChange={setCameraOpen}
        onCapture={(file, capturedAt) => onSendAttachment?.(file, { capturedAt })}
      />

      <CrmChatImageModal
        open={!!imageModalMessage}
        onOpenChange={(open) => !open && setImageModalMessage(null)}
        message={imageModalMessage}
      />
    </div>
  );
}
